/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { Checkbox, CheckboxProps } from 'ui/Checkbox';
import { FormControl, FormHelperText } from '@mui/material';
import {
  checkFormHasError,
  getFormHelperText,
  handleBlurFormField,
  handleChangeFormField,
} from 'ui/forms/formHelpers';
import { useField } from 'formik';
import FormControlLabel, { FormControlLabelProps } from 'ui/FormControlLabel';

export interface FormCheckboxProps extends CheckboxProps {
  label: FormControlLabelProps['label'];
  helperText?: string;
  disabled?: boolean;
  /**
   * Do not set directly. true when this is a child of a form checkbox group
   */
  group?: boolean;
}

interface WrapperProps {
  children: React.ReactNode;
  group: boolean;
  hasError: boolean;
  helperTextValue: React.ReactNode;
}

/**
 *
 * @deprecated Fine to use for Tock internal tools only. Please use `@tocktix/pantry` UI components for any new JCE admin tools.
 */
const FormCheckbox: React.FunctionComponent<React.PropsWithChildren<FormCheckboxProps>> = ({
  helperText,
  label,
  group,
  disabled,
  ...rest
}) => {
  const [field, meta] = useField(rest as any);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    handleChangeFormField(field, event, rest.onChange as any);

  const handleBlur = (event: React.FocusEvent<HTMLButtonElement>) =>
    handleBlurFormField(field, event, rest.onBlur);

  const hasError = checkFormHasError(meta);

  const helperTextValue = getFormHelperText(hasError, meta, helperText);

  return (
    <CheckboxWrapper group={!!group} hasError={hasError} helperTextValue={helperTextValue}>
      <FormControlLabel
        control={
          <Checkbox
            {...rest}
            {...field}
            checked={group ? field.value.includes(rest.value) : field.value}
            value={group ? rest.value : field.value}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        }
        label={label}
      />
    </CheckboxWrapper>
  );
};

/**
 * Conditionally apply FormControl context and helper text around a checkbox.
 * When used in a group there is no wrapper, the group is responsible
 * for the FormControl context and helper text.
 */
const CheckboxWrapper: React.FunctionComponent<React.PropsWithChildren<WrapperProps>> = ({
  children,
  group,
  hasError,
  helperTextValue,
}) => {
  return group ? (
    <>{children}</>
  ) : (
    <FormControl error={hasError}>
      {children}
      {helperTextValue && <FormHelperText>{helperTextValue}</FormHelperText>}
    </FormControl>
  );
};

export default FormCheckbox;
